import { useState } from 'react'
import ApplyDialog from './ApplyDialog'

const variants = {
  defaultClassName: `
    overflow-hidden
    shrink-0 select-none relative inline-flex justify-center items-center font-extrabold transition-all duration-200 ease-in-out
    focus:outline-none disabled:pointer-events-none disabled:!text-default-400 whitespace-nowrap
  `,
  variant: {
    primary: `
      _button-shadow bg-primary-500 text-white
      focus:ring-sky-500 active:bg-sky-700 disabled:!bg-gray-300
      before:bg-green-1 before:opacity-0 before:absolute before:inset-0
      before:transition-opacity before:duration-300 before:_button-hover-shadow
      before:content-[''] group-hover:before:opacity-100 hover:before:opacity-100
    `,
    'primary-alt': `
      _button-shadow bg-secondary-500 text-secondary-900
      focus:ring-sky-500 active:bg-sky-700 disabled:!bg-gray-300
      before:bg-green-1 before:opacity-0 before:absolute before:inset-0
      before:transition-opacity before:duration-300 before:_button-hover-shadow
      before:content-[''] hover:before:opacity-100 !rounded-full
    `,
    secondary: `
      _button-shadow bg-primary-900 text-white
      focus:ring-sky-500 active:bg-sky-700 disabled:!bg-gray-300
      before:bg-blue-1 before:opacity-0 before:absolute before:inset-0
      before:transition-opacity before:duration-300 before:_button-hover-shadow
      before:content-[''] hover:before:opacity-100
    `,
    third: `
      bg-transparent text-sky-500 hover:text-sky-600 hover:underline
      focus:ring-sky-500 active:text-sky-700
      disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:no-underline
    `,
    'third-dark': `
      bg-transparent text-gray-700 hover:text-gray-900 hover:underline
      focus:ring-gray-500 active:text-black
      disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:no-underline
    `,
    'third-white': `
      bg-transparent text-white hover:text-gray-200 hover:underline
      focus:ring-white active:text-gray-300
      disabled:text-gray-500 disabled:hover:text-gray-500 disabled:hover:no-underline
    `,
    outline: `
      bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400
      focus:ring-gray-500 active:bg-gray-100 active:scale-95
      disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:hover:border-gray-200
    `,
    link: `
      bg-transparent text-[--theme-text] hover:text-[--theme-text-hover]
      disabled:text-gray-400 disabled:hover:text-gray-400 disabled:hover:no-underline
      before:content-[""] before:absolute before:w-full before:transition-all before:duration-200
      before:left-0 before:bottom-0 before:border-b-2 before:border-[--theme-text] hover:before:w-0
      !px-0 !rounded-none !py-2
    `
  },
  size: {
    sm: 'px-4 pt-1.5 pb-2 text-xs rounded-[4px]',
    base: 'px-6 pt-2.5 pb-3 text-base rounded-md',
    lg: 'px-7 pt-[15px] pb-[19px] text-base rounded-lg',
    xl: 'px-8 pt-[17px] pb-[21px] text-base rounded-lg',
    icon: 'p-2',
    'icon-sm': 'p-1.5',
    'icon-lg': 'p-3'
  },
  mobile: {
    default: '',
    full: 'w-full sm:w-auto',
    block: 'w-full',
    responsive: 'w-full sm:w-auto sm:px-6'
  }
}

export const Button = (props) => {
  const {
    children,
    noInteraction = false,
    href = null,
    target = null,
    disabled = false,
    type = 'button',
    variant = 'primary',
    size = 'base',
    mobile = 'default',
    onClick,
    isApplyDialogTriggerButton = false,
    className = '',
    ...restProps
  } = props

  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false)

  const TagName = noInteraction ? 'span' : (href ? 'a' : 'button')

  // Check if href contains #apply-dialog
  const isApplyDialogTrigger = isApplyDialogTriggerButton || (href && href.includes('#apply-dialog'))

  const handleClick = (e) => {
    if (disabled) return

    // If this is an apply dialog trigger, prevent default navigation and open modal
    if (isApplyDialogTrigger) {
      e.preventDefault()
      setIsApplyDialogOpen(true)
      return
    }

    // Call original onClick if provided
    if (onClick) {
      onClick(e)
    }
  }

  const linkProps = href ? { href, target } : {}
  const buttonProps = (!href && !noInteraction) ? { type, disabled } : {}

  const combinedClassName = `
    ${variants.defaultClassName}
    ${variants.variant[variant] || variants.variant.primary}
    ${variants.size[size] || variants.size.base}
    ${variants.mobile[mobile] || variants.mobile.default}
    ${className}
  `.trim().replace(/\s+/g, ' ')

  return (
    <>
      <TagName
        {...linkProps}
        {...buttonProps}
        {...restProps}
        onClick={handleClick}
        className={combinedClassName}
        aria-disabled={disabled}
      >
        <span className='relative'>{children}</span>
      </TagName>
      
      {/* Render ApplyDialog if this button triggers it */}
      {isApplyDialogTrigger && (
        <ApplyDialog 
          open={isApplyDialogOpen} 
          onClose={() => setIsApplyDialogOpen(false)} 
        />
      )}
    </>
  )
}
